import React from "react";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import PendenciasFinanceiras from "../../common/query/PendenciasFinanceiras";
import { Alert } from "flowbite-react";
import {
  countDadosCadastrais,
  getColorScore,
  getImageURLScoreSerasa,
} from "../../../utils/utils";

const Field = ({ label, value }) => (
  <div>
    <span className="block text-sm text-gray-500">{label}</span>
    <span className="font-semibold text-sm">{value ?? "Não Informado"}</span>
  </div>
);

const PendenciasSPCSerasaPF = ({ data, dataFull }) => {
  const cadastro = data?.dadosCadastrais;
  const endereco = data?.endereco;

  const totalDadosCadastrais = Array.isArray(dataFull)
    ? dataFull.reduce((acc, item) => acc + countDadosCadastrais(item), 0)
    : countDadosCadastrais(dataFull);

  // Só renderiza se houver exatamente 1
  const podeRenderizarDadosCadastrais = totalDadosCadastrais < 2;

  /* ===== helpers de formatação (cole antes do return do componente) ===== */
  const formatDateBR = (d) => {
    if (!d) return "";
    const s = String(d).slice(0, 10);
    const [y, m, day] = s.split("-");
    if (!y || !m || !day) return s;
    return `${day}/${m}/${y}`;
  };

  const onlyDigits = (v) => String(v || "").replace(/\D/g, "");
  const formatCPF = (v) =>
    onlyDigits(v).replace(/(\d{3})(\d{3})(\d{3})(\d{2}).*/, "$1.$2.$3-$4");
  const formatCNPJ = (v) =>
    onlyDigits(v).replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/,
      "$1.$2.$3/$4-$5"
    );

  const formatCpfCnpj = (cad) =>
    cad?.cpf ? formatCPF(cad.cpf) : cad?.cnpj ? formatCNPJ(cad.cnpj) : "";

  const chip = (text) =>
    text ? (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 border border-gray-200">
        {text}
      </span>
    ) : null;

  return (
    <>
      {data?.statusRetorno === 1 && (
        <>
          {/* DADOS CADASTRAIS */}
          {/* ===== bloco melhorado ===== */}
          {cadastro && podeRenderizarDadosCadastrais && (
            <>
              <TitleStripe>Dados Cadastrais</TitleStripe>

              {/* === Seção única: Cadastro & Endereço === */}
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                {/* header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-4 bg-gradient-to-r from-gray-50 to-white border-b">
                  <div className="space-y-1">
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Resumo do cadastro
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-base font-semibold text-gray-900">
                        {cadastro?.cpf ? "Pessoa Física" : "Pessoa Jurídica"}
                      </span>
                      {chip(cadastro?.cpf ? "PF" : "PJ")}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {chip(formatCpfCnpj(cadastro))}
                    {cadastro?.sexo && chip(cadastro.sexo)}
                    {cadastro?.estadoCivil && chip(cadastro.estadoCivil)}
                  </div>
                </div>

                {/* conteúdo */}
                <div className="p-5 space-y-3">
                  {/* Bloco: Dados principais */}
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-800">
                        Dados principais
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="rounded-xl bg-gray-50/60 border border-gray-200 p-4 space-y-4">
                        <Field
                          label={
                            cadastro?.cpf ? "Nome completo" : "Razão Social"
                          }
                          value={
                            cadastro?.cpf
                              ? cadastro?.nome ?? "—"
                              : cadastro?.razaoSocial ?? "—"
                          }
                        />
                        {cadastro?.nomeMae && (
                          <Field
                            label="Nome da mãe"
                            value={cadastro?.nomeMae}
                          />
                        )}

                        {cadastro?.telefoneCelular && (
                          <Field
                            label="Celular"
                            value={cadastro?.telefoneCelular}
                          />
                        )}
                        {cadastro?.telefoneFixo && (
                          <Field
                            label="Telefone Fixo"
                            value={cadastro?.telefoneFixo}
                          />
                        )}

                        {data?.endereco?.endereco && (
                          <Field
                            label="Endereço"
                            value={data?.endereco?.endereco}
                          />
                        )}
                      </div>

                      <div className="rounded-xl bg-gray-50/60 border border-gray-200 p-4 space-y-4">
                        <Field
                          label={cadastro?.cpf ? "CPF" : "CNPJ"}
                          value={formatCpfCnpj(cadastro) || "—"}
                        />
                        {cadastro?.cpf && (
                          <Field
                            label="Data de nascimento"
                            value={
                              cadastro?.dataNascimento
                                ? formatDateBR(cadastro?.dataNascimento)
                                : "—"
                            }
                          />
                        )}
                        {cadastro?.sexo && (
                          <Field label="Sexo" value={cadastro?.sexo} />
                        )}

                        {cadastro?.estadoCivil && (
                          <Field
                            label="Estado civil"
                            value={cadastro?.estadoCivil}
                          />
                        )}

                        {cadastro?.email && (
                          <Field label="E-mail" value={cadastro?.email} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* PENDÊNCIAS SERASA */}
          <div>
            <TitleStripe className="mb-5">Pendências Ativas</TitleStripe>

            {data?.pendenciasSerasa?.length > 0 ? (
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <PendenciasFinanceiras
                    quantidadeTotal={
                      data?.resumoConsulta?.serasa?.quantidadeTotal
                    }
                    valorTotal={+data?.resumoConsulta?.serasa?.valorTotal}
                    pendenciasFinanceiras={data?.pendenciasSerasa?.map(
                      (pend) => ({
                        data: pend?.dataInclusao,
                        tipoPendencia: pend?.tipo,
                        contrato: pend?.contrato,
                        informante: pend?.credor,
                        valor: pend?.valor,
                        cidade: pend?.cidade,
                      })
                    )}
                    temDataDisponibilidade={false}
                    temContrato={false}
                  />
                </div>
              </div>
            ) : (
              <NoOcurrence />
            )}
          </div>

          {/* RESTRIÇÕES SPC */}
          <div>
            <TitleStripe className="mb-5">Restrições em aberto</TitleStripe>

            {data?.pendenciasSpc?.length > 0 ? (
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <PendenciasFinanceiras
                    quantidadeTotal={data?.resumoConsulta?.spc?.quantidadeTotal}
                    valorTotal={+data?.resumoConsulta?.spc?.valorTotal}
                    pendenciasFinanceiras={data?.pendenciasSpc?.map((pend) => ({
                      data: pend?.dataInclusao,
                      tipoPendencia: pend?.tipo,
                      contrato: pend?.contrato,
                      informante: pend?.credor,
                      valor: pend?.valor,
                      cidade: pend?.cidadeUfOrigem,
                    }))}
                    temDataDisponibilidade={false}
                  />
                </div>
              </div>
            ) : (
              <NoOcurrence />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default PendenciasSPCSerasaPF;
